import { lazy } from 'react';
import { useMemo, useRef } from 'react';
import {
    BlockWrapper,
    smartProps,
    defineBlock,
    useChildrenBlocks,
    useEditor,
    useInspectorControl,
} from '@smartfire/js-block-engine';
import { EditableHeading, EditableText } from '@smartfire/components';
import clsx from 'clsx';
import style from './block.module.scss';

const Inspector = lazy(() => import('./inspector.js'));

const Summary = ({ anchorId }) => {
    useInspectorControl(Inspector);
    const { isEditing } = useEditor();
    const { Child, Children, children } = useChildrenBlocks();
    const ref = useRef(null);

    const childrenNb = useMemo(() => {
        return children?.length ?? ref.current?.childElementCount ?? 0;
        // eslint-disable-next-line
    }, [children, ref.current]); // Must use ref.current dependency which gives a warning. It doesn't work with ref as a dependency

    return (
        <BlockWrapper.Div id={'anchor-' + anchorId} className={style.block}>
            <div className={style.content}>
                <EditableText tagName="p" className={style.subtitle} smartProp="subtitle" placeholder="Sous-titre..." />
                <EditableHeading className={style.title} smartProp="title" placeholder="Titre..." />
                {isEditing ? (
                    <Children
                        ref={ref}
                        className={clsx(
                            style.blocks,
                            childrenNb > 6 && style.moreThanSix,
                            childrenNb > 3 && style.moreThanThree,
                        )}
                        childrenProps={{ parentRef: ref }}
                        gutenbergEditorConfig={{
                            orientation: 'horizontal',
                            allowedBlocks: ['smartfire/summary---summary-child'],
                        }}
                    />
                ) : (
                    <div
                        className={clsx(
                            style.blocks,
                            childrenNb > 6 && style.moreThanSix,
                            childrenNb > 3 && style.moreThanThree,
                        )}
                    >
                        {children.map((child, index) => (
                            <Child key={index} index={index} parentRef={ref} childIndex={index} />
                        ))}
                    </div>
                )}
            </div>
        </BlockWrapper.Div>
    );
};

export default defineBlock({
    Block: Summary,
    smartProps: {
        title: smartProps.object.withDefault({
            headingLevel: 2,
            content: '',
        }),
        subtitle: smartProps.string.withDefault(''),
        anchorId: smartProps.string.withDefault('sommaire'),
    },
    setup: {
        title: 'Sommaire',
    },
});
